import { supabase } from '../../../services/supabase/client'
import type { AuthChangeEvent, User, Session, AuthError } from '@supabase/supabase-js'

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterCredentials extends LoginCredentials {
  fullName: string
}

export interface AuthResponse {
  user: User | null
  session: Session | null
  error: AuthError | null
}

/**
 * Authentication service
 * Handles all auth operations via Supabase
 */
export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    })

    return {
      user: data.user,
      session: data.session,
      error,
    }
  },

  /**
   * Register a new staff user. Never pass a role: the auth mirror trigger
   * ignores user metadata and makes every self-signup an arborist
   */
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: {
          full_name: credentials.fullName,
        },
      },
    })

    return {
      user: data.user,
      session: data.session,
      error,
    }
  },

  /**
   * Send a one-time login code to a client's email (Phase 6 portal).
   * shouldCreateUser is off: only provisioned users can sign in.
   */
  async sendOtpCode(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    })
    return { error }
  },

  /**
   * Exchange the emailed one-time code for a session
   */
  async verifyOtpCode(email: string, token: string): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    return {
      user: data.user,
      session: data.session,
      error,
    }
  },

  /**
   * Logout current user
   */
  async logout(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  /**
   * Get current session
   */
  async getSession(): Promise<{ session: Session | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.getSession()
    return {
      session: data.session,
      error,
    }
  },

  /**
   * Get current user
   */
  async getUser(): Promise<{ user: User | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.getUser()
    return {
      user: data.user,
      error,
    }
  },

  /**
   * Refresh session
   */
  async refreshSession(): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.refreshSession()
    return {
      user: data.user,
      session: data.session,
      error,
    }
  },

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error }
  },

  /**
   * Update password
   */
  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })
    return { error }
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (session: Session | null, event: AuthChangeEvent) => void) {
    // The event matters: PASSWORD_RECOVERY fires when detectSessionInUrl
    // consumes a recovery token, and is the only signal that a session is a
    // reset-in-progress rather than a normal sign-in. Dropping it is what
    // made a recovery link land on a plain login form.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      callback(session, event)
    })

    return data.subscription
  },

  /**
   * Get user's role from the database
   */
  async getUserRole(
    userId: string
  ): Promise<{ role: 'admin' | 'arborist' | 'client' | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.from('users').select('role').eq('id', userId).single()

      if (error) {
        console.error('Error fetching user role:', error)
        return { role: null, error }
      }

      return { role: data?.role || null, error: null }
    } catch (error) {
      console.error('Exception fetching user role:', error)
      return { role: null, error: error as Error }
    }
  },
}
